import React from 'react';
import PropTypes from 'prop-types';
import { FormGroup, ControlLabel, FormControl, Col, Row } from 'react-bootstrap';
import { create as createBraintreeDropIn } from 'braintree-web-drop-in';

import FilterSelect from 'components/search_sidebar/filter_select';
import ManagedInput from 'components/form/managed_input';
import { getLocation } from 'helpers/location_helper';

const CONTAINER_ID = 'braintree-drop-in-container';

export const DEFAULT_ADDRESS = {
  firstName: '',
  lastName: '',
  streetAddress: '',
  extendedAddress: '',
  locality: '',
  region: '',
  postalCode: '',
  countryCode: 'US'
};

export default class Form extends React.Component {

  static propTypes = {
    address: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      streetAddress: PropTypes.string.isRequired,
      extendedAddress: PropTypes.string.isRequired,
      locality: PropTypes.string.isRequired,
      region: PropTypes.string.isRequired,
      countryCode: PropTypes.string.isRequired,
      postalCode: PropTypes.string.isRequired
    }),
    countryOptions: PropTypes.array.isRequired,
    gatewayClientToken: PropTypes.string.isRequired,
    stateOptions: PropTypes.array.isRequired,
    vaultManager: PropTypes.bool,
    onAddressChange: PropTypes.func.isRequired,
    onClearPaymentNonce: PropTypes.func.isRequired,
    onRequestPaymentMethodNonce: PropTypes.func.isRequired
  }

  state= {
    selectedState: null
  }

  componentDidMount() {
    const { gatewayClientToken } = this.props;

    if (gatewayClientToken) this.mountBraintree();
  }

  componentWillUnmount() {
    if (this.braintree) this.braintree.teardown();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps?.address?.countryCode !== prevState?.countryCode) {
      return { selectedState: nextProps.countryOptions.filter(
        country => country['id'] == nextProps.address.countryCode)[0].states };
    } else return null;
  }

  mountBraintree() {
    const { gatewayClientToken, vaultManager, onClearPaymentNonce, onRequestPaymentMethodNonce } = this.props;
    const braintreeOptions = { authorization: gatewayClientToken, container: `#${CONTAINER_ID}`, vaultManager };

    createBraintreeDropIn(braintreeOptions).then((braintree) => {
      braintree.on('paymentMethodRequestable', () => onRequestPaymentMethodNonce(braintree));
      braintree.on('noPaymentMethodRequestable', onClearPaymentNonce);

      if (braintree.isPaymentMethodRequestable()) onRequestPaymentMethodNonce(braintree);
      this.braintree = braintree;
    });
  }

  handleChange = e => this.props.onAddressChange(e.target.name, e.target.value)

  handleStateChange = e => this.props.onAddressChange(e.target.name, e.target.value === '--' ? '' : e.target.value)

  handleChangeCountrySelect = (e) => {
    const targetValue = (e.target.name, e.target.value);
    this.setCountry(targetValue);
  }

  setCountry = (selectedCountry) => {
    const { countryOptions } = this.props;
    if (countryOptions) {
      const states = countryOptions.filter(country => country['id'] == selectedCountry)[0].states;
      this.setState({
        selectedState: states
      }, () => {
        this.props.onAddressChange('countryCode', selectedCountry);
      });
    }
  }

  handleZipChange = (zip) => {
    this.props.onAddressChange('postalCode', zip);

    if (zip.length === 5) {
      getLocation(zip).then((location) => {
        const { city, state } = location;

        if (city && state) {
          this.props.onAddressChange('locality', city);
          this.props.onAddressChange('region', state);
        }
      });
    }
  }

  renderAddressForm() {
    const { address, countryOptions, onAddressChange } = this.props;
    const { selectedState } = this.state;

    if (!address) return;

    return (
      <div className="mt-5">
        <Row>
          <Col md={6}>
            <FormGroup>
              <ControlLabel>First Name *</ControlLabel>
              <FormControl
                name="firstName"
                placeholder="First Name"
                value={address.firstName}
                onChange={this.handleChange}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <ControlLabel>Last Name *</ControlLabel>
              <FormControl
                name="lastName"
                placeholder="Last Name"
                value={address.lastName}
                onChange={this.handleChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <div>
          <FilterSelect
            className="default-country"
            label="Country *"
            name="countryCode"
            options={countryOptions}
            value={address.countryCode}
            onChange={this.handleChangeCountrySelect}
          />
        </div>
        <FormGroup>
          <ControlLabel>Address Line 1 *</ControlLabel>
          <FormControl
            name="streetAddress"
            placeholder="Address Line 1"
            value={address.streetAddress}
            onChange={this.handleChange}
          />
        </FormGroup>
        <FormGroup>
          <ControlLabel>Address Line 2</ControlLabel>
          <FormControl
            name="extendedAddress"
            placeholder="Address Line 2"
            value={address.extendedAddress}
            onChange={this.handleChange}
          />
        </FormGroup>
        <Row>
          <Col md={4}>
            <ControlLabel>City *</ControlLabel>
            <ManagedInput
              className="form-control"
              name="city"
              placeholder="City"
              value={address.locality}
              onValueChange={value => onAddressChange('locality', value)}
            />
          </Col>
          <Col md={4}>
            <FilterSelect
              label="State *"
              name="region"
              options={selectedState}
              placeholder="--"
              value={address.region}
              onChange={this.handleStateChange}
            />
          </Col>
          <Col md={4}>
            <ControlLabel>Postal Code *</ControlLabel>
            <ManagedInput
              className="form-control"
              name="zip"
              placeholder="Postal Code"
              value={address.postalCode}
              onValueChange={this.handleZipChange}
            />
          </Col>
        </Row>
      </div>
    );
  }

  render() {
    return (
      <React.Fragment>
        <div id={CONTAINER_ID} />
        {this.renderAddressForm()}
      </React.Fragment>
    );
  }
}
